import { Navigate, Link } from "react-router-dom";
import { Zap, ShieldCheck, Gift, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useVisitorAuth } from "@/context/VisitorAuthContext";

export default function SignInPage() {
    const { visitor, loading } = useVisitorAuth();

    if (loading) return null;
    if (visitor) return <Navigate to="/my-account" replace />;

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/my-account";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen" data-testid="sign-in-page">
            <Navbar />
            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-mono uppercase tracking-wider">
                            Sign in · find your options
                        </span>
                        <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                            Sign up and find your options.
                        </h1>
                        <p className="mt-4 text-slate-300 text-base leading-relaxed">
                            One click gets you a saved account so you can skip the form next time, track
                            your appointment, see your referral code, and (optionally) check if there are
                            other bills we can help you save on. No pressure — opt in only to what you want.
                        </p>

                        <div className="mt-8 flex flex-col gap-3">
                            <button
                                data-testid="google-signin-btn"
                                onClick={handleGoogle}
                                className="group flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-slate-900 font-semibold rounded-xl px-5 py-3.5 shadow-lg shadow-black/20 transition hover:scale-[1.01]"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden>
                                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.3 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.9 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19c10.5 0 19-8.5 19-19 0-1.3-.1-2.4-.4-3.5z" />
                                    <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.6 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.9 29 5 24 5 16.3 5 9.6 9 6.3 14.1z" />
                                    <path fill="#4CAF50" d="M24 43c5 0 9.5-1.9 12.9-5l-5.9-5c-2 1.4-4.5 2.3-7 2.3-5.4 0-9.9-3.7-11.4-8.6l-6.6 5.1C9.3 38.9 16 43 24 43z" />
                                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.8-3.7 5l5.9 5c-.4.4 6.5-4.7 6.5-14 0-1.3-.1-2.4-.4-3.5z" />
                                </svg>
                                Continue with Google
                            </button>
                            <div className="text-xs text-slate-500 text-center">
                                We only receive your name, email, and profile photo. No posting to Google.
                                You can delete your account any time by replying to our email.
                            </div>
                        </div>

                        <p className="mt-6 text-sm text-slate-400">
                            Just want to see plans? <Link to="/get-started" className="text-emerald-400 underline">Fill the quick form instead</Link>.
                        </p>
                    </div>

                    <div className="card-surface p-6 space-y-4">
                        <div className="text-xs uppercase tracking-wider text-emerald-400 font-mono">What you get</div>
                        <Benefit icon={<Zap className="w-4 h-4" />} title="One-click signup" body="Your name and email are pre-filled — just confirm ZIP and customer type." />
                        <Benefit icon={<ShieldCheck className="w-4 h-4" />} title="Private account page" body="See your appointment time, your lead status, and your share link in one place." />
                        <Benefit icon={<Gift className="w-4 h-4" />} title="Referral rewards" body="Share your personal referral code with friends — track who signed up." />
                        <Benefit icon={<Check className="w-4 h-4" />} title="Optional: find bills to save on" body="Opt in to see if we can match internet, mobile, home security, or natural gas too. Fully optional." />
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Benefit({ icon, title, body }) {
    return (
        <div className="flex items-start gap-3">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300">
                {icon}
            </div>
            <div>
                <div className="text-sm font-semibold text-white">{title}</div>
                <div className="text-xs text-slate-400 leading-relaxed">{body}</div>
            </div>
        </div>
    );
}
